/**
 * The static feature registry — the ONLY place features are wired into the app.
 *
 * ⚠️ OFF LIMITS to feature agents: do not add, remove or reorder entries, and do
 * not change the shape. Everything a feature contributes (routes, nav entries,
 * dictionaries) travels through its own `src/features/<id>/index.ts`.
 *
 * Imports are static so the bundle is deterministic and the router can be built
 * without any dynamic import boundary.
 */
import contentFeature from "@client/features/content";
import examFeature from "@client/features/exam";
import flashcardsFeature from "@client/features/flashcards";
import practiceFeature from "@client/features/practice";
import progressFeature from "@client/features/progress";
import teacherFeature from "@client/features/teacher";
import type { Role } from "@shared/api";
import type { FeatureModule, IconComponent, NavEntry } from "./types";

export const features: readonly FeatureModule[] = [
  practiceFeature,
  examFeature,
  flashcardsFeature,
  progressFeature,
  contentFeature,
  teacherFeature,
];

export const featureDictionaries = features.map((f) => f.dict);

/** Every nav entry from every feature, in display order. */
export const featureNav: NavEntry[] = features
  .flatMap((f) =>
    f.nav.map((entry) => ({ ...entry, requiresRole: entry.requiresRole ?? f.requiresRole })),
  )
  .sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.to.localeCompare(b.to));

/** What the shell and the home page need to decide whether an entry is shown. */
export interface NavVisibility {
  role?: Role | undefined;
  examActive?: boolean;
}

/** The nav entries a given user sees right now (role + exam-mode filtered). */
export function visibleNav(
  { role, examActive = false }: NavVisibility,
  entries: readonly NavEntry[] = featureNav,
): NavEntry[] {
  return entries.filter((entry) => {
    if (entry.requiresRole && role !== entry.requiresRole) return false;
    if (examActive && entry.hideDuringExam) return false;
    return true;
  });
}

/** One top-bar item: a plain link, or a labelled menu of related entries. */
export interface NavGroup {
  /** Stable key for React and for the open-menu state. */
  key: string;
  labelKey: string;
  /** Where the item itself points (the first child for a group). */
  to: string;
  icon?: IconComponent | undefined;
  entries: NavEntry[];
  grouped: boolean;
}

/** Collapses entries sharing a `groupKey` into one item, order preserved; a lone member stands alone. */
export function navGroups(entries: readonly NavEntry[]): NavGroup[] {
  const groups: NavGroup[] = [];
  const byKey = new Map<string, NavGroup>();
  for (const entry of entries) {
    if (!entry.groupKey) {
      groups.push({
        key: entry.to,
        labelKey: entry.labelKey,
        to: entry.to,
        icon: entry.icon,
        entries: [entry],
        grouped: false,
      });
      continue;
    }
    const existing = byKey.get(entry.groupKey);
    if (existing) {
      existing.entries.push(entry);
      continue;
    }
    const group: NavGroup = {
      key: entry.groupKey,
      labelKey: entry.groupKey,
      to: entry.to,
      icon: entry.icon,
      entries: [entry],
      grouped: true,
    };
    byKey.set(entry.groupKey, group);
    groups.push(group);
  }
  // A group left with one entry (the formula sheet during an exam, ruling 6) is that entry: a
  // "Bibliotek" menu holding only "Formler" would hide the one link behind a click.
  return groups.map((group) => {
    const only = group.entries[0]!;
    return group.grouped && group.entries.length === 1
      ? { ...group, key: only.to, labelKey: only.labelKey, grouped: false }
      : group;
  });
}
